/**
 * Minimal Hero
 * 
 * Simple text-only header with minimal styling.
 * Clean and focused design for content-first sites.
 */

import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Heart, Users, Calendar, ArrowRight } from "lucide-react";
import { useSiteConfig } from "@/lib/useSiteConfig";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useLayout } from "@/lib/layout-provider";
import { cn } from "@/lib/utils";
import { RichTextDisplay } from "@/components/ui/rich-text-display";

const HeroMinimal = () => {
  const { features } = useSiteSettings();
  const { currentLayout } = useLayout();
  const config = useSiteConfig();
  const { hero } = config;
  const heroConfig = currentLayout.hero;

  const heightClasses = {
    full: 'py-32 md:py-48',
    large: 'py-24 md:py-32',
    medium: 'py-16 md:py-24',
    small: 'py-12 md:py-16',
  };

  const alignmentClasses = {
    left: 'text-left items-start',
    center: 'text-center items-center',
    right: 'text-right items-end',
  };

  return (
    <section className={cn(
      "bg-background border-b border-border",
      heightClasses[heroConfig.height]
    )}>
      <div className={cn(
        "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col",
        alignmentClasses[heroConfig.contentAlignment]
      )}>
        {/* Breadcrumb-style label */}
        <div className="mb-4">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-heritage/10 text-heritage">
            {config.organizationName}
          </span>
        </div>

        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight tracking-tight">
          {hero.title}
          <span className="text-heritage"> {hero.highlightedText}</span>
        </h1>
        
        <div className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed max-w-2xl">
          <RichTextDisplay content={hero.subtitle || ''} prose={false} />
        </div>

        {/* CTA Buttons */}
        {heroConfig.showCTA && (
          <div className={cn(
            "flex flex-col sm:flex-row gap-3",
            heroConfig.contentAlignment === 'center' && "justify-center",
            heroConfig.contentAlignment === 'right' && "justify-end"
          )}>
            {features.membership.enabled && (
              <Button variant="heritage" size="lg" asChild className="group">
                <Link to="/membership">
                  <Users className="w-5 h-5" />
                  Join Us
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            )}
            
            {features.donation.enabled && (
              <Button variant="outline" size="lg" asChild>
                <Link to="/donate">
                  <Heart className="w-5 h-5" />
                  Support
                </Link>
              </Button>
            )}
            
            {features.events.enabled && (
              <Button variant="outline" size="lg" asChild>
                <Link to="/events">
                  <Calendar className="w-5 h-5" />
                  Events
                </Link>
              </Button>
            )}

            {!features.membership.enabled && !features.donation.enabled && !features.events.enabled && (
              <Button variant="heritage" size="lg" asChild className="group">
                  <Link to="/about-us">
                  Learn More
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            )}
          </div>
        )}

        {/* Minimal Statistics */}
        {heroConfig.showStats && hero.showStats && hero.stats.length > 0 && (
          <div className={cn(
            "mt-12 flex flex-wrap gap-8",
            heroConfig.contentAlignment === 'center' && "justify-center",
            heroConfig.contentAlignment === 'right' && "justify-end"
          )}>
            {hero.stats.map((stat, index) => (
              <div key={index} className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-heritage">{stat.value}</span>
                <span className="text-muted-foreground">{stat.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default HeroMinimal;




